import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { Divider, FormHelperText, Grid, Typography } from '@material-ui/core';
import * as yup from 'yup';
import { Button } from '../../../../catalyst/core';
import FormikTextField from '../../../../catalyst/core/form/TextField/variants/FormikTextField';
import FormNameField from './FormNameField';
import FormikCheckbox from '../../../../catalyst/core/form/Checkbox/variants/FormikCheckbox';
import { Alert, AlertTitle } from '@material-ui/lab';
import { useSelector } from 'react-redux';
import {
    qlikAppsDropdownSelector,
    qlikAppsSelector,
} from '../../../../redux/qlik/selectors';
import { ToastsStore } from 'react-toasts';
import api from 'api';

yup.addMethod(yup.number, 'withinRange', (min, max) => {
    return yup
        .number()
        .test(
            'range',
            `number should be between ${min} and ${max}`,
            (value) => value >= min && value <= max
        );
});

const validationSchema = {
    name: yup
        .string()
        .matches(/^[a-zA-Z0-9 ]*$/, 'Alphanumeric characters only')
        .required(),
    xs: yup.number().withinRange(1, 12).required().positive().integer(),
    sm: yup.number().withinRange(1, 12).required().positive().integer(),
    md: yup.number().withinRange(1, 12).required().positive().integer(),
    elevation: yup.number().withinRange(0, 10).required().integer(),
};

const FormCapture = ({ formEndpoint, initialValues, saveAction }) => {
    const qlikAppsList = useSelector(qlikAppsDropdownSelector);
    const [apiKey, setApiKey] = useState('{ api_key }');

    const getApiKey = () => {
        api.settings
            .getApiKey()
            .then((results) => {
                setApiKey(results.apiKey);
            })
            .catch((err) => {
                console.error(err);
                ToastsStore.error('Failed to load api key');
            });
    };

    useEffect(() => {
        getApiKey();
    }, []);

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={(values, { setSubmitting }) => {
                setSubmitting(false);
                console.log(values);
                saveAction(values);
            }}
            validationSchema={yup.object(validationSchema)}
        >
            {({ submitForm, isSubmitting, errors, touched, values }) => {
                const submit = (event) => {
                    event.preventDefault();
                    submitForm();
                };
                return (
                    <>
                        <Grid container direction="row" spacing={1}>
                            <FormNameField
                                qlikAppsList={qlikAppsList}
                                errors={errors}
                                touched={touched}
                            />
                        </Grid>
                        <Grid container direction="row" spacing={1}>
                            <Grid item>
                                <FormikCheckbox
                                    id={'persistentData'}
                                    label={'Retain previous entry'}
                                    name={'persistentData'}
                                />
                                <FormHelperText>
                                    Pre-fills the form with the last entry from
                                    the database
                                </FormHelperText>
                            </Grid>
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            style={{
                                paddingTop: '20px',
                                marginBottom: '15px',
                            }}
                        >
                            <Alert severity="info">
                                <AlertTitle>Info</AlertTitle>
                                <p style={{ marginBottom: '5px' }}>
                                    The following form data can be accessed
                                    using a secure endpoint. The key can be
                                    found in the configure systems menu under
                                    API Key.
                                </p>
                                <span
                                    style={{
                                        fontWeight: 600,
                                        marginBottom: '10px',
                                        display: 'block',
                                    }}
                                >
                                    {formEndpoint}
                                </span>
                                <p style={{ marginBottom: '5px' }}>
                                    {`api_key : ${apiKey}`}
                                </p>
                            </Alert>
                            <Divider style={{ marginBottom: '10px' }} />
                            <Typography variant={'subtitle1'}>
                                Appearance
                            </Typography>
                            <Typography vatiant={'caption'}>
                                Each grid row is split into 12 columns
                            </Typography>
                        </Grid>
                        <Grid container direction="row" spacing={1}>
                            <Grid item sm={3}>
                                <FormikTextField
                                    id={'xs'}
                                    label={'Mobile Columns'}
                                    name={'xs'}
                                    type={'number'}
                                    variant={'outlined'}
                                    fullWidth
                                    inputProps={{ min: 1, max: 12 }}
                                    error={errors.xs}
                                    touched={touched.xs}
                                    helperText={touched.xs && errors.xs}
                                />
                            </Grid>
                            <Grid item sm={3}>
                                <FormikTextField
                                    id={'sm'}
                                    label={'Tablet Columns'}
                                    name={'sm'}
                                    type={'number'}
                                    variant={'outlined'}
                                    fullWidth
                                    inputProps={{ min: 1, max: 12 }}
                                    error={errors.sm}
                                    touched={touched.sm}
                                    helperText={touched.sm && errors.sm}
                                />
                            </Grid>
                            <Grid item sm={3}>
                                <FormikTextField
                                    id={'md'}
                                    label={'Desktop Columns'}
                                    name={'md'}
                                    type={'number'}
                                    variant={'outlined'}
                                    fullWidth
                                    inputProps={{ min: 1, max: 12 }}
                                    error={errors.md}
                                    touched={touched.md}
                                    helperText={touched.md && errors.md}
                                />
                            </Grid>
                            <Grid item sm={3}>
                                <FormikTextField
                                    id={'elevation'}
                                    label={'Elevation'}
                                    name={'elevation'}
                                    type={'number'}
                                    variant={'outlined'}
                                    fullWidth
                                    inputProps={{ min: 0, max: 10 }}
                                    error={errors.elevation}
                                    touched={touched.elevation}
                                    helperText={
                                        touched.elevation && errors.elevation
                                    }
                                />
                            </Grid>
                            <Grid container direction="row" spacing={1}>
                                <Grid item>
                                    <Button
                                        onClick={submit}
                                        color={'primary'}
                                        variant={'contained'}
                                    >
                                        Save & Close
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </>
                );
            }}
        </Formik>
    );
};

export default FormCapture;

FormCapture.propTypes = {
    initialValues: PropTypes.object.isRequired,
    saveAction: PropTypes.func.isRequired,
};
